/**
 * Progress bar for horizontal resource-list carousels: syncs to slideshow-slides scroll.
 * Supports click and drag interaction to scroll the carousel.
 *
 * Visual fill starts at MIN_FILL_PERCENT (default 20%) at scroll 0 so the bar reflects
 * partial "peek" of the next slide when slides use vw widths.
 */
const MIN_FILL_PERCENT = 20;
const FILL_SPREAD = 100 - MIN_FILL_PERCENT;

class ResourceCarouselProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;
  /** @type {HTMLElement | null} */
  #fill = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {ResizeObserver | null} */
  #resizeObs = null;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {number | null} */
  #rafId = null;
  /** @type {number} */
  #targetScroll = 0;

  #onScroll = () => {
    this.#sync();
  };

  connectedCallback() {
    const host =
      this.closest('.resource-list__carousel') ||
      this.closest('.product-formulas-carousel-wrap') ||
      this.closest('.resource-list') ||
      this.parentElement;
    this.#scroller = host?.querySelector('slideshow-slides');
    this.#fill = this.querySelector('.carousel-progress__fill') ?? this.querySelector('.resource-carousel-progress__fill') ?? null;
    this.#track = this.querySelector('.carousel-progress__track') ?? this;

    if (!this.#scroller || !this.#fill) return;

    this.#scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObs = new ResizeObserver(() => this.#sync());
    this.#resizeObs.observe(this.#scroller);

    // Add click and drag interaction
    this.#track.addEventListener('pointerdown', this.#onPointerDown);

    requestAnimationFrame(() => this.#sync());
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#resizeObs?.disconnect();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#resizeObs = null;
    this.#scroller = null;
    this.#fill = null;
    this.#track = null;
  }

  /** @returns {number} */
  #maxScroll() {
    const s = this.#scroller;
    if (!s) return 0;
    return Math.max(0, s.scrollWidth - s.clientWidth);
  }

  /**
   * Calculate scroll ratio from X position on the track
   * @param {number} clientX - The X position from the event
   * @returns {number} The target scroll ratio (0-1)
   */
  #getRatioFromX(clientX) {
    const track = this.#track;
    if (!track) return 0;

    const rect = track.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  }

  /**
   * Handle pointer down on track
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!this.#scroller || !this.#track) return;

    event.preventDefault();
    this.#track.setPointerCapture(event.pointerId);

    this.#isDragging = true;
    this.#track.classList.add('is-dragging');

    // Calculate and apply initial position
    const ratio = this.#getRatioFromX(event.clientX);
    this.#targetScroll = ratio * this.#maxScroll();
    this.#scroller.scrollLeft = this.#targetScroll;
    this.#sync();

    this.#track.addEventListener('pointermove', this.#onPointerMove);
    this.#track.addEventListener('pointerup', this.#onPointerUp);
    this.#track.addEventListener('pointercancel', this.#onPointerUp);
    this.#track.addEventListener('lostpointercapture', this.#onPointerUp);
  };

  /**
   * Handle pointer move during drag
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#isDragging || !this.#scroller) return;

    const ratio = this.#getRatioFromX(event.clientX);
    this.#targetScroll = ratio * this.#maxScroll();
    this.#scroller.scrollLeft = this.#targetScroll;
    this.#sync();
  };

  /**
   * Handle pointer up / end drag
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (!this.#track) return;

    this.#isDragging = false;
    this.#track.classList.remove('is-dragging');

    try {
      this.#track.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture may already be released
    }

    this.#track.removeEventListener('pointermove', this.#onPointerMove);
    this.#track.removeEventListener('pointerup', this.#onPointerUp);
    this.#track.removeEventListener('pointercancel', this.#onPointerUp);
    this.#track.removeEventListener('lostpointercapture', this.#onPointerUp);
  };

  #sync() {
    const s = this.#scroller;
    const fill = this.#fill;
    if (!s || !fill) return;

    const max = this.#maxScroll();
    if (max <= 0) {
      fill.style.width = '0%';
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      return;
    }

    this.hidden = false;
    this.removeAttribute('aria-hidden');

    const p = s.scrollLeft / max;
    const pct = Math.max(0, Math.min(100, MIN_FILL_PERCENT + FILL_SPREAD * p));
    fill.style.width = `${pct}%`;
    this.setAttribute('aria-valuenow', String(Math.round(pct)));
  }
}

if (!customElements.get('resource-carousel-progress')) {
  customElements.define('resource-carousel-progress', ResourceCarouselProgress);
}
